import { Status } from '@rtkwlf/mr-infra-risk';
import { ArrayParam, NumberParam, StringParam } from 'use-query-params';

import type { GetRisksParams } from '@apis/mr-infra-risk/queries/use-risks';
import { GridSelectOperator } from '@packages/fenrir/components';

import type {
  RangeFilter,
  RiskParam,
} from '../../filter-management/filter-map-item';
import type { acceptedParamTypes } from '../../hooks/use-risks-management-filters/filter-types';
import {
  AssetTypeParam,
  CriticalityParam,
  DeviceCategoryParam,
  DeviceOSParam,
  ResolutionTypeParam,
  SeverityParam,
  SourceParam,
  StateParam,
  StatusParam,
} from '../../hooks/use-risks-management-filters/filter-types';
import { UnixDateParam } from '../../query-params/unix-date-param';

export const ALL_RISKS_COLUMN_DEFS = {
  ID: 'id',
  ISSUE_ID: 'issueID',
  ASSET_ID: 'assetID',
  ASSET_NAME: 'assetName',
  ASSET_CATEGORY: 'deviceCategory',
  ASSET_OS_NAME: 'deviceOSFamily',
  ASSIGNEE: 'assignee',
  NAME: 'name',
  STATE: 'state',
  STATUS: 'status',
  ASSET_CRITICALITY: 'assetImportance',
  SOURCE: 'source',
  SCORE: 'score',
  FIRST_DETECTED_TIME: 'firstDetectedTime',
  LAST_DETECTED_TIME: 'lastDetectedTime',
  SEVERITY: 'severity',
  CVSSV3: 'cvssV3Score',
  RESOLUTION_TIME: 'resolutionTime',
  RESOLUTION_TYPE: 'resolutionType',
  DEADLINE: 'deadline',
  ACTIONS: 'actions',
  CVE: 'cve',
  CISA_KEV: 'cisaKnownExploit',
  ASSET_TYPE: 'assetType',
  SEARCH: 'search', // corner case for search filter
} as const;

export const ALL_RISKS_SUPPORTED_SORT_FIELDS: string[] = [
  ALL_RISKS_COLUMN_DEFS.SCORE,
  ALL_RISKS_COLUMN_DEFS.CVSSV3,
  ALL_RISKS_COLUMN_DEFS.DEADLINE,
  ALL_RISKS_COLUMN_DEFS.FIRST_DETECTED_TIME,
  ALL_RISKS_COLUMN_DEFS.LAST_DETECTED_TIME,
  ALL_RISKS_COLUMN_DEFS.RESOLUTION_TIME,
  ALL_RISKS_COLUMN_DEFS.ASSET_CRITICALITY,
  ALL_RISKS_COLUMN_DEFS.CISA_KEV,
  ALL_RISKS_COLUMN_DEFS.ASSET_TYPE,
];

export type allRisksQueryParamType = {
  [key in keyof GetRisksParams]: acceptedParamTypes;
};

export const allRisksRangeFilters: Record<string, RangeFilter<RiskParam>> = {
  [ALL_RISKS_COLUMN_DEFS.SCORE]: {
    to: 'toScore',
    from: 'fromScore',
  },
  [ALL_RISKS_COLUMN_DEFS.CVSSV3]: {
    to: 'toCvssV3Score',
    from: 'fromCvssV3Score',
  },
  [ALL_RISKS_COLUMN_DEFS.FIRST_DETECTED_TIME]: {
    to: 'toFirstDetectedTime',
    from: 'fromFirstDetectedTime',
  },
  [ALL_RISKS_COLUMN_DEFS.LAST_DETECTED_TIME]: {
    to: 'toLastDetectedTime',
    from: 'fromLastDetectedTime',
  },
  [ALL_RISKS_COLUMN_DEFS.RESOLUTION_TIME]: {
    to: 'toResolutionTime',
    from: 'fromResolutionTime',
  },
  [ALL_RISKS_COLUMN_DEFS.DEADLINE]: {
    to: 'toDeadline',
    from: 'fromDeadline',
  },
};

export const allRisksQueryParamsMap: allRisksQueryParamType = {
  [ALL_RISKS_COLUMN_DEFS.STATE]: StateParam,
  [ALL_RISKS_COLUMN_DEFS.STATUS]: StatusParam,
  [ALL_RISKS_COLUMN_DEFS.ASSET_CRITICALITY]: CriticalityParam,
  [ALL_RISKS_COLUMN_DEFS.SOURCE]: SourceParam,
  [ALL_RISKS_COLUMN_DEFS.SEVERITY]: SeverityParam,
  [ALL_RISKS_COLUMN_DEFS.RESOLUTION_TYPE]: ResolutionTypeParam,
  [allRisksRangeFilters[ALL_RISKS_COLUMN_DEFS.SCORE].to]: NumberParam,
  [allRisksRangeFilters[ALL_RISKS_COLUMN_DEFS.SCORE].from]: NumberParam,
  [allRisksRangeFilters[ALL_RISKS_COLUMN_DEFS.CVSSV3].to]: NumberParam,
  [allRisksRangeFilters[ALL_RISKS_COLUMN_DEFS.CVSSV3].from]: NumberParam,
  [allRisksRangeFilters[ALL_RISKS_COLUMN_DEFS.FIRST_DETECTED_TIME].to]:
    UnixDateParam,
  [allRisksRangeFilters[ALL_RISKS_COLUMN_DEFS.FIRST_DETECTED_TIME].from]:
    UnixDateParam,
  [allRisksRangeFilters[ALL_RISKS_COLUMN_DEFS.LAST_DETECTED_TIME].to]:
    UnixDateParam,
  [allRisksRangeFilters[ALL_RISKS_COLUMN_DEFS.LAST_DETECTED_TIME].from]:
    UnixDateParam,
  [allRisksRangeFilters[ALL_RISKS_COLUMN_DEFS.RESOLUTION_TIME].to]:
    UnixDateParam,
  [allRisksRangeFilters[ALL_RISKS_COLUMN_DEFS.RESOLUTION_TIME].from]:
    UnixDateParam,
  [allRisksRangeFilters[ALL_RISKS_COLUMN_DEFS.DEADLINE].to]: UnixDateParam,
  [allRisksRangeFilters[ALL_RISKS_COLUMN_DEFS.DEADLINE].from]: UnixDateParam,
  [ALL_RISKS_COLUMN_DEFS.SEARCH]: StringParam,
  [ALL_RISKS_COLUMN_DEFS.ASSET_CATEGORY]: DeviceCategoryParam,
  [ALL_RISKS_COLUMN_DEFS.ASSET_OS_NAME]: DeviceOSParam,
  [ALL_RISKS_COLUMN_DEFS.ASSIGNEE]: ArrayParam,
  [ALL_RISKS_COLUMN_DEFS.CISA_KEV]: StringParam,
  [ALL_RISKS_COLUMN_DEFS.ASSET_ID]: StringParam,
  [ALL_RISKS_COLUMN_DEFS.ISSUE_ID]: StringParam,
  [ALL_RISKS_COLUMN_DEFS.ASSET_TYPE]: AssetTypeParam,
};

export const DEFAULT_FILTER = [
  {
    id: 1,
    field: ALL_RISKS_COLUMN_DEFS.STATUS,
    operator: GridSelectOperator.Is,
    value: Status.Unresolved,
  },
];

export const DEFAULT_RISKS_COLUMN_MODEL = {
  [ALL_RISKS_COLUMN_DEFS.ID]: false,
  [ALL_RISKS_COLUMN_DEFS.NAME]: true,
  [ALL_RISKS_COLUMN_DEFS.ASSET_NAME]: true,
  [ALL_RISKS_COLUMN_DEFS.ASSET_CATEGORY]: false,
  [ALL_RISKS_COLUMN_DEFS.ASSET_OS_NAME]: false,
  [ALL_RISKS_COLUMN_DEFS.STATE]: true,
  [ALL_RISKS_COLUMN_DEFS.STATUS]: false,
  [ALL_RISKS_COLUMN_DEFS.ASSET_CRITICALITY]: false,
  [ALL_RISKS_COLUMN_DEFS.SOURCE]: true,
  [ALL_RISKS_COLUMN_DEFS.SCORE]: true,
  [ALL_RISKS_COLUMN_DEFS.FIRST_DETECTED_TIME]: true,
  [ALL_RISKS_COLUMN_DEFS.LAST_DETECTED_TIME]: false,
  [ALL_RISKS_COLUMN_DEFS.SEVERITY]: false,
  [ALL_RISKS_COLUMN_DEFS.CVSSV3]: false,
  [ALL_RISKS_COLUMN_DEFS.RESOLUTION_TIME]: false,
  [ALL_RISKS_COLUMN_DEFS.RESOLUTION_TYPE]: false,
  [ALL_RISKS_COLUMN_DEFS.DEADLINE]: false,
  [ALL_RISKS_COLUMN_DEFS.ASSIGNEE]: false,
  [ALL_RISKS_COLUMN_DEFS.CVE]: false,
  [ALL_RISKS_COLUMN_DEFS.CISA_KEV]: true,
  [ALL_RISKS_COLUMN_DEFS.ACTIONS]: true,
  [ALL_RISKS_COLUMN_DEFS.ASSET_ID]: false,
  [ALL_RISKS_COLUMN_DEFS.ISSUE_ID]: false,
  [ALL_RISKS_COLUMN_DEFS.ASSET_TYPE]: false,
};

export const EXPORT_RISK_API_NAMES = {
  [ALL_RISKS_COLUMN_DEFS.ASSET_CATEGORY]: 'device.category',
  [ALL_RISKS_COLUMN_DEFS.ASSET_OS_NAME]: 'device.os.family',
  [ALL_RISKS_COLUMN_DEFS.CVSSV3]: 'cve.primary.cvss.v3.score',
  [ALL_RISKS_COLUMN_DEFS.RESOLUTION_TIME]: 'resolution.timestamp',
  [ALL_RISKS_COLUMN_DEFS.ASSIGNEE]: 'management.assignee',
  [ALL_RISKS_COLUMN_DEFS.DEADLINE]: 'management.deadline',
  [ALL_RISKS_COLUMN_DEFS.CVE]: 'cve.primary.id',
};
